import { navigate } from "@reach/router";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { Post } from "../../utils/api";
import { getQuery } from "../../utils/query";
import useFocus from "../../utils/use-focus";
import Button from "../shared/Button";
import FormField from "../shared/FormField";
import { EmailIcon, EmailNormalIcon, KeyIcon, LoginIcon, PasswordIcon, RegisterIcon, SpinnerIcon, TimesIcon, UserIcon } from "../shared/Icon";
import TextInput from "../shared/TextInput";
import "./LoginForm.css";
import Cookies from "js-cookie";

enum Form {
	Login,
	Register,
	ForgotPassword,
	ResetPassword,
}

interface IProps {
	isOpen: boolean;
	onClose(): void;
	onLogin(): void;
}

interface ILoginRequest {
	email: string;
	password: string;
}

interface IRegisterRequest {
	username: string;
	email: string;
	password: string;
	accessKey: string;
}

interface IForgotPasswordRequest {
	email: string;
}

interface IResetPasswordRequest {
	id: number;
	newPassword: string;
	token: string;
}

const LoginForm: FunctionComponent<IProps> = (props) => {
	const { isOpen, onClose, onLogin } = props;

	const action = getQuery("action");
	const id = parseInt(getQuery("id") ?? "0");
	const token = getQuery("token") ?? "";
	const defaultForm = action === "resetPassword" ? Form.ResetPassword : Form.Login;

	const [form, setForm] = useState(defaultForm);
	const [error, setError] = useState<string>("");
	const [username, setUsername] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [password, setPassword] = useState<string>("");
	const [password2, setPassword2] = useState<string>("");
	const [accessKey, setAccessKey] = useState<string>("");
	const [isSubmitting, setSubmitting] = useState(false);

	const setUsernameCallback = useCallback((value: string) => setUsername(value), [setUsername]);
	const setEmailCallback = useCallback((value: string) => setEmail(value), [setEmail]);
	const setPasswordCallback = useCallback((value: string) => setPassword(value), [setPassword]);
	const setPassword2Callback = useCallback((value: string) => setPassword2(value), [setPassword2]);
	const setAccessKeyCallback = useCallback((value: string) => setAccessKey(value), [setAccessKey]);

	const [htmlRef, setFocus] = useFocus();
	//useEffect(() => setFocus(), [setFocus]);

	const validateUsername = useMemo(() => {
		if (form === Form.Login) { return ""; }
		if (username.length === 0) { return ""; }
		if (username.length < 1 || username.length > 16) {
			return "Username must be 1-16 characters";
		}
		if (/\s+/.test(username)) {
			return "Username may not contain whitespace";
		}
		return "";
	}, [form, username]);
	const validatePassword = useMemo(() => {
		if (form === Form.Login) { return ""; }
		if (password.length === 0) { return ""; }
		// if (!/^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,}$/.test(password)) {
		if (!/^.{6,}$/.test(password)) {
			return <>
				Password must
				<ul>
					<li>Contain atleast 6 characters</li>
					{/* <li>At least one uppercase character</li>
					<li>At least one lowercase character</li>
					<li>At least one digit</li>
					<li>At least one special characters</li> */}
				</ul>
			</>;
		}
	}, [form, password]);
	const validatePassword2 = useMemo(() => {
		if (password.length === 0) { return ""; }
		if (password !== password2) {
			return "Passwords must match";
		}
	}, [password, password2]);
	const validateAccessKey = useMemo(() => {
		if (accessKey.length === 0) { return ""; }
	}, [accessKey.length]);

	const login = useCallback(() => {
		if (!email || !password) { return; }
		Cookies.remove("access_token");
		Cookies.remove("xsrf_token");
		return Post<ILoginRequest, void>("/auth/authenticate", {
			email: email,
			password: password,
		})
			.then(() => {
				onLogin();
				navigate("");
			})
			.catch(ex => setError(ex?.message));
	}, [email, password, onLogin]);
	const canRegister = useMemo(() => {
		return !(!username || !email || !password || !password2 || !accessKey
			|| !!validateUsername || !!validatePassword || !!validatePassword2 || !!validateAccessKey)
	}, [accessKey, email, password, password2, username, validateAccessKey, validatePassword, validatePassword2, validateUsername]);
	const register = useCallback(() => {
		if (!canRegister) { return; }
		return Post<IRegisterRequest, void>("/auth/register", {
			username: username,
			email: email,
			password: password,
			accessKey: accessKey,
		})
			.then(() => {
				onLogin();
				navigate("");
			})
			.catch(ex => setError(ex?.message));
	}, [canRegister, email, onLogin, password, username, accessKey]);
	const forgotPassword = useCallback(() => {
		if (!email) { return; }
		return Post<IForgotPasswordRequest, void>("/auth/forgotPassword", {
			email: email,
		})
			.then(() => {
				setError("Check email for password reset.  Email is only sent if there is an account with this email.");
			})
			.catch(ex => setError(ex?.message));
	}, [email]);
	const resetPassword = useCallback(() => {
		if (!password || !password2 || !!validatePassword || !!validatePassword2) { return; }
		return Post<IResetPasswordRequest, void>("/auth/resetPassword", {
			id: id,
			newPassword: password,
			token: token,
		})
			.then(() => {
				setForm(Form.Login);
				setError("Password reset");
			})
			.catch(ex => setError(ex?.message));
	}, [id, password, password2, token, validatePassword, validatePassword2]);
	const submit = useCallback(() => {
		if (isSubmitting) { return; }
		if (form === Form.Login) {
			if (!email || !password) { return; }
			setSubmitting(true);
			setError("");
			return login()?.then(() => setSubmitting(false));
		}
		else if (form === Form.Register) {
			if (!canRegister) { return; }
			setSubmitting(true);
			setError("");
			register()?.then(() => setSubmitting(false));
		}
		else if (form === Form.ForgotPassword) {
			if (!email) { return; }
			setSubmitting(true);
			setError("");
			return forgotPassword()?.then(() => setSubmitting(false));
		}
		else if (form === Form.ResetPassword) {
			if (!password || !password2 || !!validatePassword || !!validatePassword2) { return; }
			setSubmitting(true);
			setError("");
			return resetPassword()?.then(() => setSubmitting(false));
		}
	}, [isSubmitting, form, email, password, login, canRegister, register, forgotPassword, password2, validatePassword, validatePassword2, resetPassword]);

	const closeLogin = useCallback(() => {
		setUsername("");
		setEmail("");
		setPassword("");
		setPassword2("");
		setAccessKey("");
		setError("");
		setForm(Form.Login);
		onClose();
	}, [onClose, setUsername, setEmail, setPassword, setPassword2, setAccessKey, setError]);


	const loginOnClick = useCallback(() => {
		if (isSubmitting) { return; }
		if (form === Form.Login) {
			return submit();
		}
		setForm(Form.Login);
		setFocus();
	}, [setFocus, submit, setForm, form, isSubmitting]);

	const buttonLevel = (isPrimary: boolean) => {
		return isPrimary ? "Primary" : "Secondary";
	}

	const loginButton = useMemo(() => {
		const isLogin = form === Form.Login;
		const text = isLogin ? "Log in" : "Switch to log in";
		const icon = isLogin ? <LoginIcon /> : undefined;
		const level = buttonLevel(isLogin);
		const disabled = isLogin && (!email || !password);
		const inProgress = isLogin && isSubmitting;
		return <Button size="Large" color="Green" level={level} onClick={loginOnClick} leftIcon={icon} disabled={disabled} inProgress={inProgress}>
			{text}
		</Button>;
	}, [form, loginOnClick, email, password, isSubmitting]);

	const registerOnClick = useCallback(() => {
		if (isSubmitting) { return; }
		if (form === Form.Register) {
			return submit();
		}
		setForm(Form.Register);
	}, [submit, setForm, form, isSubmitting]);

	const registerButton = useMemo(() => {
		if (form === Form.ResetPassword) { return <></>; }
		const isRegister = form === Form.Register;
		const text = isRegister ? "Register" : "New? Register";
		const icon = isRegister ? <RegisterIcon /> : undefined;
		const level = buttonLevel(isRegister);
		const disabled = isRegister
			&& (!canRegister);
		const inProgress = isRegister && isSubmitting;
		return <Button size="Large" color="Green" level={level} onClick={registerOnClick} leftIcon={icon} disabled={disabled} inProgress={inProgress}>
			{text}
		</Button>;
	}, [form, canRegister, isSubmitting, registerOnClick]);

	const resetPasswordButton = useMemo(() => {
		const isFocued = form === Form.ResetPassword;
		if (!isFocued) { return <></>; }
		const disabled = !password || !password2 || !!validatePassword || !!validatePassword2;
		return <Button size="Large" color="Green" level="Primary" onClick={submit} leftIcon={<PasswordIcon />} disabled={disabled} inProgress={isSubmitting}>
			Reset Password
		</Button>;
	}, [form, password, password2, validatePassword, validatePassword2, submit, isSubmitting]);

	const usernameField = useMemo(() => {
		if (form !== Form.Register) { return; }
		return <FormField
			icon={<UserIcon />}
			input={<TextInput type="text" label={<><UserIcon />Username</>} value={username} setValue={setUsernameCallback} onEnter={submit} error={validateUsername} focus={isOpen && form === Form.Register} />}
		/>
	}, [form, username, setUsernameCallback, submit, validateUsername, isOpen]);

	const emailField = useMemo(() => {
		if (form === Form.ResetPassword) { return; }
		return <FormField
			icon={<EmailIcon />}
			input={<TextInput autocomplete="email" id="userEmail" type="text" label={<><EmailIcon />E-mail</>} value={email} setValue={setEmailCallback} htmlRef={htmlRef} onEnter={submit} required focus={isOpen && (form === Form.Login || form === Form.ForgotPassword)} />}
		/>
	}, [form, email, setEmailCallback, htmlRef, submit, isOpen]);

	const passwordField = useMemo(() => {
		if (form === Form.ForgotPassword) { return; }
		const autocompete = form === Form.Login ? "password" : "new-password";
		return <FormField
			icon={<PasswordIcon />}
			input={<TextInput type="password" autocomplete={autocompete} label={<><PasswordIcon />Password</>} value={password} setValue={setPasswordCallback} onEnter={submit} error={validatePassword} required focus={isOpen && form === Form.ResetPassword} />}
		/>
	}, [form, isOpen, password, setPasswordCallback, submit, validatePassword]);

	const password2Field = useMemo(() => {
		if (form !== Form.Register && form !== Form.ResetPassword) { return; }
		return <FormField
			icon={<PasswordIcon />}
			input={<TextInput type="password" autocomplete="new-password" label={<><PasswordIcon />Confirm Password</>} value={password2} setValue={setPassword2Callback} error={validatePassword2} onEnter={submit} />}
		/>
	}, [password2, setPassword2Callback, submit, validatePassword2, form]);

	const accessKeyField = useMemo(() => {
		if (form !== Form.Register) { return; }
		return <FormField
			icon={<KeyIcon />}
			input={<TextInput type="password" label={<><KeyIcon />Access Key</>} value={accessKey} setValue={setAccessKeyCallback} error={validateAccessKey} onEnter={submit} />}
		/>
	}, [form, accessKey, setAccessKeyCallback, validateAccessKey, submit]);

	const forgotPasswordOnClick = useCallback(() => {
		if (isSubmitting) { return; }
		if (form === Form.ForgotPassword) {
			return submit();
		}
		setForm(Form.ForgotPassword);
		setFocus();
	}, [form, isSubmitting, setFocus, submit]);

	const forgotPasswordButton = useMemo(() => {
		const isToggled = form === Form.ForgotPassword;
		const text = isToggled ? "Send reset email" : "Forgot Password?";
		const icon = isToggled ? isSubmitting ? <SpinnerIcon /> : <EmailNormalIcon /> : undefined;
		const level = isToggled ? "Primary" : "Tertiary";
		const disabled = isToggled
			&& (!email);
		return <Button size="Medium" color="Blue" level={level} leftIcon={icon} disabled={disabled} onClick={forgotPasswordOnClick}>
			{text}
		</Button>;
	}, [email, forgotPasswordOnClick, form, isSubmitting]);

	return (<div id="loginForm" className={"loginModal modal " + ((isOpen) ? "focused" : "")}>
		<div id="authForm" className="modal-content animate">
			<div className="container">
				<div id="errors">
					{!!error && <p className="errorText">{error}</p>}
				</div>
				{usernameField}
				{emailField}
				{passwordField}
				{password2Field}
				{accessKeyField}
				<div className="loginButtons">
					{loginButton}
					{registerButton}
					{resetPasswordButton}
				</div>
			</div>

			<div className="container loginBottom">
				<Button size="Medium" color="Red" level="Secondary" onClick={closeLogin} leftIcon={<TimesIcon />}>
					Cancel
				</Button>
				{forgotPasswordButton}
			</div>
		</div>
	</div>);
}

export default LoginForm;